import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from "react-dom";
import { useNavigate, Link, useLocation } from 'react-router-dom';
import API_BASE_URL from "../../Config";
import { v4 as uuidv4 } from "uuid";
import CityPicker from "../CityPicker";
import { isDoctorPublic } from "../../utils/doctorVisibility";
import "../styles/Front_Header.css";
// NO external cartEvents import - everything inline

// Tries a list of possible localStorage keys and returns the first non-empty value found.
// This exists because we don't know the exact key names your login page uses for
// name/email/phone yet - once you confirm them, you can simplify this back to a
// single localStorage.getItem("exact_key").
const getFirstAvailable = (keys) => {
  for (const key of keys) {
    const val = localStorage.getItem(key);
    if (val && val !== "null" && val !== "undefined") return val;
  }
  return null;
};

const useScrollToTop = () => {
  const { pathname, key } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const root = document.getElementById("root");
    if (root) root.scrollTop = 0;
  }, [pathname, key]);
};

const SearchDropdown = ({ show, results, position, onItemClick }) => {
  if (!show) return null;
  if (!results.products.length && !results.doctors.length && !results.clinics.length && !results.treatments?.length) return null;

  const labelStyle = {
    padding: "8px 14px 4px", fontSize: "10px", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: ".09em",
    color: "#aaa", background: "#fff",
    position: "sticky", top: 0, borderBottom: "1px solid #f5f5f5",
    display: "block",
  };
  const rowBase = {
    display: "flex", alignItems: "center", gap: 10, padding: "8px 14px",
    textDecoration: "none", color: "#222", cursor: "pointer",
    transition: "background .13s",
  };
  const imgStyle = {
    width: 40, height: 40, objectFit: "cover",
    borderRadius: 8, border: "1px solid #eee",
    background: "#f5f5f5", flexShrink: 0,
  };

  return ReactDOM.createPortal(
    <div style={{
      position: "fixed", top: position.bottom + 4, left: position.left,
      width: Math.max(position.width, 320), zIndex: 999999,
      background: "#fff", borderRadius: 14,
      boxShadow: "0 12px 40px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.07)",
      border: "1px solid #f0f0f0", maxHeight: 400, overflowY: "auto",
      animation: "hdrDropIn 0.17s ease",
    }}>
      {results.products.length > 0 && (
        <>
          <span style={labelStyle}>Products</span>
          {results.products.slice(0, 6).map((p) => (
            <Link key={p.id} to={`/product/${p.slug}`} style={rowBase}
              onMouseDown={(e) => e.preventDefault()} onClick={() => onItemClick(p.name)}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f7faf8"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
              <img src={p.images ? `${API_BASE_URL}public/productimages/${p.images.split(",")[0]}` : `${API_BASE_URL}public/noimage.jpg`} alt={p.name} style={imgStyle} />
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</span>
                <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "1px 6px", width: "fit-content", background: "#e8f5e9", color: "#2e7d32" }}>Product</span>
              </div>
            </Link>
          ))}
        </>
      )}
      {results.doctors.length > 0 && (
        <>
          <span style={labelStyle}>Doctors</span>
          {results.doctors.slice(0, 6).map((d) => (
            <Link key={d.id} to={`/doctordetails/${d.slug}`} style={rowBase}
              onMouseDown={(e) => e.preventDefault()} onClick={() => onItemClick(d.name)}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f7faf8"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
              <img src={d.images ? `${API_BASE_URL}public/doctorimages/${d.images.split(",")[0]}` : `${API_BASE_URL}public/noimage.jpg`} alt={d.name} style={imgStyle} />
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.name}</span>
                <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "1px 6px", width: "fit-content", background: "#e3f2fd", color: "#1565c0" }}>Doctor</span>
              </div>
            </Link>
          ))}
        </>
      )}
      {results.clinics.length > 0 && (
        <>
          <span style={labelStyle}>Clinics</span>
          {results.clinics.slice(0, 6).map((c) => (
            <Link key={c.id} to={`/clinicdetails/${c.slug}`} style={rowBase}
              onMouseDown={(e) => e.preventDefault()} onClick={() => onItemClick(c.name)}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f7faf8"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
              <img src={c.images ? `${API_BASE_URL}public/clinicimages/${c.images.split(",")[0]}` : `${API_BASE_URL}public/noimage.jpg`} alt={c.name} style={imgStyle} />
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</span>
                <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "1px 6px", width: "fit-content", background: "#fdeef0", color: "#c2185b" }}>Clinic</span>
              </div>
            </Link>
          ))}
        </>
      )}
      {results.treatments?.length > 0 && (
        <>
          <span style={labelStyle}>Treatments</span>
          {results.treatments.slice(0, 6).map((t) => (
            <Link key={t.id} to={`/treatment/${t.slug}`} style={rowBase}
              onMouseDown={(e) => e.preventDefault()} onClick={() => onItemClick(t.name)}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f7faf8"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
              <img src={t.image ? `${API_BASE_URL}public/treatment/${t.image.split(",")[0]}` : (t.icon ? `${API_BASE_URL}public/treatment_icon/${t.icon.split(",")[0]}` : `${API_BASE_URL}public/noimage.jpg`)} alt={t.name} style={imgStyle} />
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.name}</span>
                <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "1px 6px", width: "fit-content", background: "#fff3e0", color: "#e65100" }}>Treatment</span>
              </div>
            </Link>
          ))}
        </>
      )}
    </div>,
    document.body
  );
};

const SearchInput = ({ allProductsRef, allDoctorsRef, allClinicsRef, allTreatmentsRef, placeholder = "Search Here", className = "", style = {}, onAfterNavigate }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ products: [], doctors: [], clinics: [], treatments: [] });
  const [showDrop, setShowDrop] = useState(false);
  const [pos, setPos] = useState({ bottom: 0, left: 0, width: 0 });
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const mouseInDrop = useRef(false);
  const navigate = useNavigate();

  const updatePos = useCallback(() => {
    if (!inputRef.current) return;
    const r = inputRef.current.getBoundingClientRect();
    setPos({ bottom: r.bottom, left: r.left, width: r.width });
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", updatePos, { passive: true });
    window.addEventListener("resize", updatePos);
    return () => { window.removeEventListener("scroll", updatePos); window.removeEventListener("resize", updatePos); };
  }, [updatePos]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults({ products: [], doctors: [], clinics: [], treatments: [] }); setShowDrop(false); return; }
    debounceRef.current = setTimeout(() => {
      const lq = q.toLowerCase();
      const res = {
        products: allProductsRef.current.filter((p) => p?.name?.toLowerCase().includes(lq)),
        doctors: allDoctorsRef.current.filter((d) => d?.name?.toLowerCase().includes(lq)),
        clinics: (allClinicsRef?.current || []).filter((c) => c?.name?.toLowerCase().includes(lq)),
        treatments: (allTreatmentsRef?.current || []).filter((t) => t?.name?.toLowerCase().includes(lq)),
      };
      setResults(res);
      setShowDrop(res.products.length > 0 || res.doctors.length > 0 || res.clinics.length > 0 || res.treatments.length > 0);
      updatePos();
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, updatePos]);

  useEffect(() => {
    const onDown = (e) => { if (!wrapRef.current) return; if (wrapRef.current.contains(e.target)) return; setShowDrop(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const handleItemClick = (name) => { setQ(name); setShowDrop(false); if (onAfterNavigate) onAfterNavigate(); };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && q.trim()) { navigate(`/category/?search=${encodeURIComponent(q)}`); setShowDrop(false); if (onAfterNavigate) onAfterNavigate(); }
    if (e.key === "Escape") setShowDrop(false);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%", display: "flex", alignItems: "center" }}>
      <input ref={inputRef} type="text" value={q} placeholder={placeholder}
        autoComplete="off" className={className} style={style}
        onChange={(e) => { setQ(e.target.value); updatePos(); }}
        onFocus={() => { updatePos(); if (q.trim()) setShowDrop(true); }}
        onBlur={() => { setTimeout(() => { if (!mouseInDrop.current) setShowDrop(false); }, 200); }}
        onKeyDown={handleKeyDown}
      />
      <div onMouseEnter={() => { mouseInDrop.current = true; }} onMouseLeave={() => { mouseInDrop.current = false; }}>
        <SearchDropdown show={showDrop} results={results} position={pos} onItemClick={handleItemClick} />
      </div>
    </div>
  );
};

const HeaderSearchOverlay = ({ visible, anchorRect, allProductsRef, allDoctorsRef, allClinicsRef, allTreatmentsRef }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ products: [], doctors: [], clinics: [], treatments: [] });
  const [showDrop, setShowDrop] = useState(false);
  const [dropPos, setDropPos] = useState({ bottom: 0, left: 0, width: 0 });
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const mouseInDrop = useRef(false);

  const updateDropPos = useCallback(() => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    setDropPos({ bottom: r.bottom, left: r.left, width: r.width });
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults({ products: [], doctors: [], clinics: [], treatments: [] }); setShowDrop(false); return; }
    debounceRef.current = setTimeout(() => {
      const lq = q.toLowerCase();
      const res = {
        products: allProductsRef.current.filter((p) => p?.name?.toLowerCase().includes(lq)),
        doctors: allDoctorsRef.current.filter((d) => d?.name?.toLowerCase().includes(lq)),
        clinics: (allClinicsRef?.current || []).filter((c) => c?.name?.toLowerCase().includes(lq)),
        treatments: (allTreatmentsRef?.current || []).filter((t) => t?.name?.toLowerCase().includes(lq)),
      };
      setResults(res);
      setShowDrop(res.products.length > 0 || res.doctors.length > 0 || res.clinics.length > 0 || res.treatments.length > 0);
      updateDropPos();
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, updateDropPos]);

  useEffect(() => { if (!visible) { setQ(""); setShowDrop(false); } }, [visible]);

  if (!anchorRect) return null;
  return (
    <div
      ref={wrapRef}
      className="header-search-overlay"
      style={{
        position: "fixed",
        top: anchorRect.top,
        // left: anchorRect.left,
        // width: anchorRect.width,
        height: anchorRect.height,
        zIndex: 1060,
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
        transition: "opacity .15s ease",
      }}
    >
      <i className="fa-solid fa-search" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "rgb(157 132 68)", zIndex: 1, fontSize: "15px" }} />
      <input className="rounded-pill"
        type="text" value={q} placeholder="Search medicines, doctors, clinics..." autoComplete="off"
        style={{ width: "100%", height: "100%", outline: "none", background: "white", fontSize: "inherit", padding: "0 12px 0 44px", boxSizing: "border-box", border: "solid 1px rgb(221 221 221)" }}
        onChange={(e) => { setQ(e.target.value); updateDropPos(); }}
        onFocus={() => { if (q.trim()) { setShowDrop(true); updateDropPos(); } }}
        onBlur={() => { setTimeout(() => { if (!mouseInDrop.current) setShowDrop(false); }, 200); }}
        onKeyDown={(e) => e.key === "Escape" && setShowDrop(false)}
      />

      <div onMouseEnter={() => { mouseInDrop.current = true; }} onMouseLeave={() => { mouseInDrop.current = false; }}>
        <SearchDropdown
          show={showDrop}
          results={results}
          position={dropPos}
          onItemClick={(name) => { setQ(name); setShowDrop(false); }}
        />
      </div>
    </div>
  );
};

const Header = () => {
  useScrollToTop();
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/" || location.pathname === "/home";

  // Runs on EVERY route change, no matter what triggered the navigation.
  // This is the safety net for the "page stuck dimmed/unclickable" bug:
  // if the offcanvas menu is closed right before navigating to a route
  // where Header (and the offcanvas DOM) unmounts, Bootstrap's own
  // transition-based cleanup of body/backdrop never gets to run because
  // the element it was watching is gone. So on every route change we
  // forcibly clear any leftover backdrop + body lock styles ourselves.
  useEffect(() => {
    document.querySelectorAll('.wsmenu-list > li').forEach(el => {
      el.classList.remove('active', 'open');
    });
    document.querySelectorAll('.wsshoptabing, .sub-menu').forEach(el => {
      el.style.removeProperty('display');
      el.style.removeProperty('visibility');
      el.style.removeProperty('opacity');
    });
    document.querySelectorAll('.modal-backdrop, .dropdown-backdrop, .offcanvas-backdrop').forEach(el => el.remove());
    document.body.classList.remove('modal-open', 'wsactive', 'offcanvas-backdrop');
    document.body.style.removeProperty('padding-right');
    document.body.style.removeProperty('overflow');
  }, [location.pathname]);

  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    setIsScrolled(false);
  }, [location.pathname]);
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 24);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const [banners, setBanners] = useState([]);
  const [userId, setUserId] = useState(localStorage.getItem("user_id"));

  // Flexible lookups: tries every common key variation your login/register
  // flow might use, so this works even before you confirm the exact keys.
  // Once confirmed, you can simplify each line back to a single getItem call.
  const [userName, setUserName] = useState(
    getFirstAvailable(["user_name", "userName", "name", "full_name", "fullname", "user_full_name"])
  );
  const [userEmail, setUserEmail] = useState(
    getFirstAvailable(["user_email", "userEmail", "email"])
  );
  const [userPhone, setUserPhone] = useState(
    getFirstAvailable([
      "user_phone", "userPhone", "phone", "mobile", "user_mobile",
      "mobile_number", "phone_number", "mobileNo", "phoneNo",
      "mobile_no", "phone_no", "contact", "contact_number",
      "contactNumber", "user_contact", "whatsapp_number",
      "whatsappNumber", "msisdn"
    ])
  );

  const [menuData, setMenuData] = useState(null);
  const allProductsRef = useRef([]);
  const allDoctorsRef = useRef([]);
  const allClinicsRef = useRef([]);
  const allTreatmentsRef = useRef([]);

  const [cartCount, setCartCount] = useState(() => {
    const saved = localStorage.getItem("cart_count");
    return saved ? Number(saved) : 0;
  });

  useEffect(() => {
    const handler = (e) => {
      const n = Number(e?.detail?.count ?? 0);
      setCartCount(n);
      localStorage.setItem("cart_count", String(n));
    };
    window.addEventListener("cart:updated", handler);
    return () => window.removeEventListener("cart:updated", handler);
  }, []);

  const tamilNaduCities = [
    "Chennai", "Coimbatore", "Madurai", "Trichy", "Salem", "Tirunelveli", "Erode",
    "Vellore", "Thoothukudi", "Dindigul", "Thanjavur", "Ranipet", "Sivakasi", "Karur",
    "Udhagamandalam", "Hosur", "Nagercoil", "Kanchipuram", "Kumarapalayam", "Karaikudi",
    "Neyveli", "Cuddalore", "Kumbakonam", "Tiruvannamalai", "Pollachi", "Rajapalayam",
    "Gudiyatham", "Pudukkottai", "Vaniyambadi", "Ambur", "Nagapattinam", "Viluppuram",
  ];

  const [selectedCity, setSelectedCity] = useState("Detecting location…");
  const [detectingGPS, setDetectingGPS] = useState(false);

  useEffect(() => { detectCurrentLocation(); }, []);

  /* Reverse-geocode via Nominatim, trying progressively wider zoom levels
     until a usable place name comes back — down to village/hamlet/suburb
     level — instead of forcing a match against the fixed 32-city list.
     (Previously this only accepted the result if it exactly matched one
     of the hardcoded Tamil Nadu city names, so any real address that
     resolved to a neighbourhood/village — i.e. almost always — silently
     fell back to "Chennai" and looked like GPS detection was broken.)

     Label is built as "<small place>, <city>" (e.g. "Iyyapanthangal,
     Chennai"), matching Home.js's detectHeroLocation exactly so both
     pickers behave identically. */
  const detectCurrentLocation = () => {
    if (!navigator.geolocation) { setSelectedCity("Chennai"); return; }
    setDetectingGPS(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        const zoomLevels = [14, 16, 13, 18, 10];
        let resolved = false;

        for (const zoom of zoomLevels) {
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=${zoom}&addressdetails=1`
            );
            const data = await res.json();
            const a = data.address || {};

            const small =
              a.neighbourhood || a.suburb || a.quarter || a.residential ||
              a.village || a.hamlet || a.city_district || a.borough || "";

            const city =
              a.city || a.town || a.municipality || a.county || "";

            let label = "";
            if (small && city && small !== city) {
              label = `${small}, ${city}`;
            } else if (small) {
              label = small;
            } else if (city) {
              label = city;
            }

            if (label) {
              setSelectedCity(label);
              resolved = true;
              break;
            }
          } catch (err) {
            console.log(`[location] zoom=${zoom} fetch failed:`, err);
          }
        }

        if (!resolved) {
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=16&addressdetails=1`
            );
            const data = await res.json();
            if (data.display_name) {
              const parts = data.display_name.split(",").map((p) => p.trim());
              const label = /^\d+$/.test(parts[0]) ? parts.slice(1, 3).join(", ") : parts.slice(0, 2).join(", ");
              if (label) {
                setSelectedCity(label);
                resolved = true;
              }
            }
          } catch (err) {
            console.log("[location] display_name fallback failed:", err);
          }
        }

        if (!resolved) setSelectedCity("Chennai");
        setDetectingGPS(false);
      },
      (err) => {
        console.log("[location] geolocation error:", err);
        setSelectedCity("Chennai");
        setDetectingGPS(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}api/getproductsAll`)
      .then(r => r.json())
      .then(json => { if (json?.status && Array.isArray(json.data)) allProductsRef.current = json.data; })
      .catch(e => console.log("Header products:", e));
  }, []);

  /* settingsFront/1 already returns menuData PLUS the full clinic list AND
     the full treatment list — no need for a second network call for either.
     This is also where allClinicsRef gets populated now (previously it was
     wrongly filled from api/treatmentList, which returns treatments, not
     clinics — that's why clinic names weren't showing up in search).
     allTreatmentsRef is populated from the same payload's data.treatment,
     the exact same field the nav menu already uses (menuData?.treatment). */
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}api/settingsFront/1`);
        const data = await res.json();
        if (data.status) {
          setMenuData(data.data);
          allClinicsRef.current = data.data.clinic || [];       // real clinic data
          allTreatmentsRef.current = data.data.treatment || []; // real treatment data (for search)
        }
      } catch (err) { console.error(err); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const all = []; let page = 1, last = 1;
      try {
        do {
          const r = await fetch(`${API_BASE_URL}api/doctorFrontList?page=${page}&minPrice=0&maxPrice=10000000`);
          const json = await r.json();
          if (!json.status) break;
          all.push(...(json.data.data || []));
          last = json.data.last_page || 1; page++;
        } while (page <= 1);
        allDoctorsRef.current = all.filter(isDoctorPublic);
      } catch (e) { console.log("Header doctors:", e); }
    })();
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}api/bannerData`)
      .then(r => r.json())
      .then(d => setBanners(Array.isArray(d) ? d : (d.data || [])))
      .catch(e => console.log(e));
  }, []);

  let deviceId = localStorage.getItem("device_id");
  if (!deviceId) { deviceId = uuidv4(); localStorage.setItem("device_id", deviceId); }

  useEffect(() => {
    fetch(`${API_BASE_URL}api/getCart`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_id: localStorage.getItem("device_id") }),
    })
      .then(r => r.json())
      .then(j => {
        const count = j.count ?? 0;
        window.dispatchEvent(new CustomEvent("cart:updated", { detail: { count } }));
      })
      .catch(e => console.error(e));
  }, []);

  const closeMobileSearch = () => {
    const el = document.getElementById("searchOffcanvas");
    if (el && window.bootstrap?.Offcanvas) {
      const inst = window.bootstrap.Offcanvas.getInstance(el);
      if (inst) inst.hide();
    }
  };

  // Closes the mobileMenu offcanvas AND force-removes any leftover
  // .offcanvas-backdrop / body-lock styles Bootstrap may leave behind.
  // This handles the immediate click; the pathname-based useEffect above
  // is the backup in case navigation happens before this fully settles.
  const closeMobileMenu = () => {
    const el = document.getElementById("mobileMenu");
    if (el && window.bootstrap?.Offcanvas) {
      const inst = window.bootstrap.Offcanvas.getInstance(el);
      if (inst) inst.hide();
    }
    document.querySelectorAll('.offcanvas-backdrop').forEach(b => b.remove());
    document.body.classList.remove('offcanvas-backdrop', 'wsactive');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
  };

  // Manually closes the offcanvas then navigates - avoids relying on
  // data-bs-dismiss, whose Bootstrap click handler can race with
  // React Router's navigation and make the link feel unresponsive.
  const handleLoginClick = () => {
    closeMobileMenu();
    setTimeout(() => navigate("/login-register"), 150);
  };

  // Clears all stored user/session data and redirects to login
  const handleLogout = () => {
    [
      "user_id",
      "user_name", "userName", "name", "full_name", "fullname", "user_full_name",
      "user_email", "userEmail", "email",
      "user_phone", "userPhone", "phone", "mobile", "user_mobile",
      "mobile_number", "phone_number", "mobileNo", "phoneNo",
      "mobile_no", "phone_no", "contact", "contact_number",
      "contactNumber", "user_contact", "whatsapp_number",
      "whatsappNumber", "msisdn"
    ].forEach((key) => localStorage.removeItem(key));
    setUserId(null);
    setUserName(null);
    setUserEmail(null);
    setUserPhone(null);
    closeMobileMenu();
    setTimeout(() => navigate("/login-register"), 150);
  };

  const [anchorRect, setAnchorRect] = useState(null);
  const originalInputRef = useRef(null);

  useEffect(() => {
    const setup = () => {
      const input = document.querySelector('.search-here-anchor');
      if (!input) return false;
      originalInputRef.current = input;
      const r = input.getBoundingClientRect();
      setAnchorRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      const onResize = () => {
        const r2 = input.getBoundingClientRect();
        setAnchorRect({ top: r2.top, left: r2.left, width: r2.width, height: r2.height });
      };
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    };
    let cleanup = null;
    const run = () => { cleanup = setup() || null; };
    run();
    if (!originalInputRef.current) {
      const t1 = setTimeout(() => { run(); if (!originalInputRef.current) { const t2 = setTimeout(run, 800); return () => clearTimeout(t2); } }, 400);
      return () => clearTimeout(t1);
    }
    return () => { cleanup && cleanup(); };
  }, []);

  useEffect(() => {
    if (!originalInputRef.current) return;
    const r = originalInputRef.current.getBoundingClientRect();
    setAnchorRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [isScrolled]);

  return (
    <>
      <style>{`
        @keyframes hdrDropIn {
          from { opacity:0; transform:translateY(5px); }
          to   { opacity:1; transform:translateY(0);   }
        }
        @media (max-width: 1199.98px) {
          .header-search-overlay {
            display: none !important;
          }
        }
      `}</style>

      <header
        style={isScrolled ? { position: "sticky", top: 0, zIndex: 1050 } : { position: "sticky", top: 0,zIndex:2  }}
        className={isScrolled ? "hc-header-scrolled" : ""}
      >
        {!isHomePage && (
          <HeaderSearchOverlay
            visible={true}
            anchorRect={anchorRect}
            allProductsRef={allProductsRef}
            allDoctorsRef={allDoctorsRef}
            allClinicsRef={allClinicsRef}
            allTreatmentsRef={allTreatmentsRef}
          />
        )}

        <div className='container-fluid p-xl-0'>
         <div className='row'>
          <div  className='col-2 p-0 text-center d-xl-block d-none '>
              <Link to="/" className='logo-new'><img src="./front/assets/images/logo.png" alt="" /></Link>
          </div>
           <div  className='col-12 col-xl-10 p-xl-0'>
        <div className="primary-header header-box d-xl-block d-none">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-xl-5 col-xxl-4 my-auto">
                <div className="logo-block" style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <CityPicker cities={tamilNaduCities} value={selectedCity} onChange={setSelectedCity} onDetectGPS={detectCurrentLocation} detecting={detectingGPS} />
                </div>
              </div>
              <div className="col-xl-2 col-xxl-4 my-auto">
                <div className="search-here-anchor" style={{ width: "100%", height: "36px", visibility: "hidden" }} />
              </div>
              <div className="col-xl-5 col-xxl-3 my-auto">
                <div className="cart-block justify-content-end">
                  {userId
                    ? <Link to="/myprofile" className="btn transparent desktop-show"><img src="./front/assets/images/user.svg" alt="" /> My Profile</Link>
                    : <Link to="/login-register" className="btn transparent desktop-show"><img src="./front/assets/images/user.svg" alt="" /> Login/ Signup</Link>
                  }
                  <Link to="/ayush-product-list?type=offer" className="transparent"><img src="./front/assets/images/offer.svg" alt="" /> Offers</Link>
                  <Link to="/cart" className="btn position-relative desktop-show header-cart-icon">
                    <img src="./front/assets/images/cart.svg" alt="" />
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                      <span className="cartcount">{cartCount}</span>
                      <span className="visually-hidden">unread messages</span>
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="secondary-header d-none d-xl-block">
          <div className="container">
            <div className="row">
              <div className="col-12 d-xl-block d-none">
                <div className="wsmobileheader clearfix">
                  <a id="wsnavtoggle" className="wsanimated-arrow"><span></span></a>
                  <div className="wssearch clearfix">
                    <i className="wsopensearch fas fa-search"></i>
                    <i className="wsclosesearch fas fa-times"></i>
                    <div className="wssearchform clearfix"><form><input type="text" placeholder="Search Here" /></form></div>
                  </div>
                </div>
                <div className="headtoppart clearfix">
                  <div className="headerwp clearfix"><div className="headertopleft clearfix"></div></div>
                  <div className="headerfull">
                    <div className="wsmain clearfix">
                      <nav className="wsmenu clearfix">
                        <ul className="wsmenu-list">
                          <li><a href="#" className="navtext"><span>Home</span></a></li>
                          <li aria-haspopup="true">
                            <a href="#" className="navtext"><span>Medicine</span></a>
                            <div className="wsshoptabing wtsdepartmentmenu clearfix">
                              <div className="wsshopwp clearfix" style={{ height: "400px" }}>
                                <ul className="wstabitem clearfix">
                                  <li className="wsshoplink-active">
                                    <a href="#">Health Condition</a>
                                    <div className="wstitemright clearfix">
                                      <div className="container-fluid">
                                        <div className="row">
                                          <div className="col-lg-3 col-md-12 clearfix categories_list_items">
                                            <ul className="wstliststy02 clearfix">
                                              <li className="wstheading clearfix">Personal Care</li>
                                              {menuData?.personal_care?.map((item) => (<li key={item.id}><Link to={`/category/${item.slug}`}>{item.name}</Link></li>))}
                                            </ul>
                                          </div>
                                          <div className="col-lg-3 col-md-12 clearfix categories_list_items">
                                            <ul className="wstliststy02 clearfix">
                                              <li className="wstheading clearfix">Liver Care</li>
                                              {menuData?.liver_care?.map((item) => (<li key={item.id}><Link to={`/category/${item.slug}`}>{item.name}</Link></li>))}
                                            </ul>
                                          </div>
                                          <div className="col-lg-3 col-md-12 clearfix categories_list_items">
                                            <ul className="wstliststy02 clearfix">
                                              <li className="wstheading clearfix">OTC Product</li>
                                              {menuData?.otc_product?.map((item) => (<li key={item.id}><Link to={`/product/${item.slug}`}>{item.name}</Link></li>))}
                                            </ul>
                                          </div>
                                          <div className="col-lg-3 col-md-12 clearfix categories_list_items">
                                            <ul className="wstliststy02 clearfix">
                                              <li className="wstheading clearfix">Vitamin &amp; Supplement</li>
                                              {menuData?.vitamin?.map((item) => (<li key={item.id}><Link to={`/category/${item.slug}`}>{item.name}</Link></li>))}
                                            </ul>
                                          </div>
                                          <div className="col-lg-12 wstadsize01 clearfix mt-3">
                                            {banners.filter(b => b.banner_type === "menu" && b.status === "Active").map(b => (
                                              <a key={b.id} href={b.link}><img src={`${API_BASE_URL}public/banner/${b.image}`} alt="" /></a>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </li>
                                  <li></li>
                                </ul>
                                <div><Link to="/ayush-product-list" className="all-medicine text-dark m-4 d-block">All mediation</Link></div>
                              </div>
                            </div>
                          </li>
                          <li><Link className="navtext" to="/clinic-list-page"><span>Find Clinic</span></Link></li>
                          <li><Link className="navtext" to="/wellness"><span>Wellness</span></Link></li>
                          <li><Link className="navtext" to="/doctor-list-page"><span>Consult a doctor</span></Link></li>
                          <li aria-haspopup="true">
                            <Link to="/treatments/" className="navtext"><span>Treatments</span></Link>
                            <ul className="sub-menu">
                              {menuData?.treatment?.map((item) => (<li key={item.id}><Link to={`/treatment/${item.slug}`}>{item.name}</Link></li>))}
                              <li><Link to="/treatments/" className="navtext"><span>All Treatments</span></Link></li>
                            </ul>
                          </li>
                          <li><Link to="/blog" className="navtext"><span>Health Insights</span></Link></li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mobile-header d-xl-none">
          <div className="d-flex justify-content-between align-items-center p-2 h-100">
            <div className="d-flex align-items-center justify-content-center gap-3">
              <button className="btn breadcrumb-icon" type="button" data-bs-toggle="offcanvas" data-bs-target="#mobileMenu">
                <i className="fa fa-bars"></i>
              </button>
              <Link to="/"><img src="./front/assets/images/logo.png" alt="" style={{ height: "35px" }} className="logo" /></Link>
            </div>
            <ul className="list__icon d-flex align-items-center justify-content-center gap-3 pe-3">
              <li>
                <button className="btn p-0" type="button" data-bs-toggle="offcanvas" data-bs-target="#searchOffcanvas" style={{ background: "none", border: "none", color: "inherit" }}>
                  <i className="fa-solid fa-magnifying-glass" style={{ color: "#79716B" }}></i>
                </button>
              </li>
              <li>
                {isHomePage ? (
                  <CityPicker cities={tamilNaduCities} value={selectedCity} onChange={setSelectedCity} onDetectGPS={detectCurrentLocation} detecting={detectingGPS} />
                ) : (
                  <Link to="/cart" className="btn position-relative p-0 header-cart-icon" style={{ background: "none", border: "none" }}>
                    <img src="./front/assets/images/cart.svg" alt="Cart" style={{ height: "24px", color: "#79716B" }} />
                    {cartCount > 0 && (
                      <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: "10px" }}>
                        <span className="cartcount">{cartCount}</span>
                        <span className="visually-hidden">unread messages</span>
                      </span>
                    )}
                  </Link>
                )}
              </li>
            </ul>
          </div>

          <div className="offcanvas offcanvas-start" tabIndex="-1" id="mobileMenu">
            <div className="offcanvas-header border-bottom">
              {userId ? (
                <div className="d-flex align-items-center justify-content-between w-100">
                  <Link
                    to="/myprofile"
                    className="d-flex align-items-center gap-3 text-decoration-none text-dark"
                    onClick={closeMobileMenu}
                    style={{ minWidth: 0 }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle fw-bold"
                      style={{ width: 56, height: 56, background: "#1a1a1a", color: "#fff", fontSize: 24, flexShrink: 0 }}
                    >
                      {(userName || "U").charAt(0).toUpperCase()}
                    </div>
                    <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                      <span className="fw-semibold text-truncate" style={{ fontSize: 15 }}>{userName || "My Account"}</span>
                      {userEmail && <span className="text-truncate" style={{ fontSize: 13, color: "#333" }}>{userEmail}</span>}
                      {userPhone && <span className="text-truncate" style={{ fontSize: 13, color: "#333" }}>{userPhone}</span>}
                    </div>
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="btn d-flex align-items-center gap-1"
                    style={{ background: "none", border: "none", color: "#c0392b", fontSize: 13, fontWeight: 600, flexShrink: 0, padding: "6px 8px" }}
                    title="Logout"
                  >
                    <i className="fa-solid fa-right-from-bracket"></i>
                    Logout
                  </button>
                </div>
              ) : (
                <Link
                  to="/login-register"
                  onClick={(e) => { e.preventDefault(); handleLoginClick(); }}
                  className="d-flex align-items-center gap-3 text-decoration-none"
                >
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle"
                    style={{ width: 48, height: 48, background: "#1a1a1a", flexShrink: 0 }}
                  >
                    <i className="fa-regular fa-user" style={{ color: "#fff", fontSize: 20 }}></i>
                  </div>
                  <span
                    className="d-flex align-items-center justify-content-center rounded-pill fw-semibold"
                    style={{
                      background: "#1a1a1a",
                      color: "#fff",
                      padding: "10px 22px",
                      fontSize: 14,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Sign in / Join
                  </span>
                </Link>
              )}
              <button type="button" className="btn-close ms-2" onClick={closeMobileMenu}></button>
            </div>
            <div className="offcanvas-body">
              <p className="my-3 fw-medium">Offerings</p>
              <div className="grid-2">
                <Link to="/clinic-list-page" className="mobile-menu-card" onClick={closeMobileMenu}><div className="mobile-menu-circle-card mobile-menu-circle-bg d-flex align-items-center justify-content-center mb-2"><img src="./front/assets/images/clinic.svg" alt="" /></div><span>Find Clinic</span></Link>
                <Link to="/wellness" className="mobile-menu-card" onClick={closeMobileMenu}><div className="mobile-menu-circle-card mobile-menu-circle-bg d-flex align-items-center justify-content-center mb-2"><img src="./front/assets/images/clinic.svg" alt="" /></div><span>Wellness</span></Link>
                <Link to="/doctor-list-page" className="mobile-menu-card" onClick={closeMobileMenu}><div className="mobile-menu-circle-card mobile-menu-circle-bg-2 d-flex align-items-center justify-content-center mb-2"><img src="./front/assets/images/sethoscope.svg" alt="" /></div><span>Consult a doctor</span></Link>
                <Link to="/treatments" className="mobile-menu-card" onClick={closeMobileMenu}><div className="mobile-menu-circle-card mobile-menu-circle-bg-3 d-flex align-items-center justify-content-center mb-2"><img src="./front/assets/images/heart.svg" alt="" /></div><span>Treatment</span></Link>
                <Link to="/blog" className="mobile-menu-card" onClick={closeMobileMenu}><div className="mobile-menu-circle-card mobile-menu-circle-bg-4 d-flex align-items-center justify-content-center mb-2"><img src="./front/assets/images/blog.svg" alt="" /></div><span>Health blog</span></Link>
                <Link to="#" className="mobile-menu-card" onClick={closeMobileMenu}><div className="mobile-menu-circle-card mobile-menu-circle-bg-5 d-flex align-items-center justify-content-center mb-2"><img src="./front/assets/images/smile.svg" alt="" /></div><span>Care plan</span></Link>
              </div>
              <p className="my-3 fw-medium">Buy Medicines</p>
              <div className="grid-2">
                <Link to="/category/shree-varma-herbal-320" className="mobile-menu-card" onClick={closeMobileMenu}><div className="mobile-menu-circle-card mobile-menu-circle-bg-6 d-flex align-items-center justify-content-center mb-2"><img src="./front/assets/images/tablet.svg" alt="" /></div><span>Medicine</span></Link>
                {/* <Link to="/blog" className="mobile-menu-card">
                <div className="mobile-menu-circle-card mobile-menu-circle-bg d-flex align-items-center justify-content-center mb-2"><img src="./front/assets/images/heart-red.svg" alt="" /></div><span>Health Insights</span></Link> */}
              </div>
              <p className="my-3 fw-medium">About Us</p>
              <div className="grid-2">
                <Link to="/about" className="mobile-menu-card" onClick={closeMobileMenu}><div className="mobile-menu-circle-card mobile-menu-circle-bg-7 d-flex align-items-center justify-content-center mb-2"><img src="./front/assets/images/info-icon.svg" alt="" /></div><span>About us</span></Link>
                <Link to="#" className="mobile-menu-card" onClick={closeMobileMenu}><div className="mobile-menu-circle-card mobile-menu-circle-bg-2 d-flex align-items-center justify-content-center mb-2"><img src="./front/assets/images/support.svg" alt="" /></div><span>Support</span></Link>
              </div>
              <button className="dc-book-btn w-100 my-3 d-flex align-items-center justify-content-center gap-2">
                {userId
                  ? <Link to="/myprofile" className="text-white d-flex gap-2" onClick={closeMobileMenu}><img src="./front/assets/images/user-mobile.svg" alt="" />My Profile</Link>
                  : <Link
                      to="/login-register"
                      onClick={(e) => { e.preventDefault(); handleLoginClick(); }}
                      className="text-white d-flex gap-2"
                    >
                      <img src="./front/assets/images/user-mobile.svg" alt="" />Login/ Signup
                    </Link>
                }
              </button>
            </div>
          </div>

          <div className="offcanvas offcanvas-top" tabIndex="-1" id="searchOffcanvas" style={{ height: "auto" }}>
            <div className="offcanvas-header border-bottom">
              <h6 className="fw-semibold mb-0">Search</h6>
              <button type="button" className="btn-close" data-bs-dismiss="offcanvas"></button>
            </div>
            <div className="offcanvas-body py-3">
              <div className="input-group" style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <i className="fa-solid fa-search" style={{ position: "absolute", left: 14, zIndex: 10, color: "#999", pointerEvents: "none", fontSize: 14 }} />
                <SearchInput allProductsRef={allProductsRef} allDoctorsRef={allDoctorsRef} allClinicsRef={allClinicsRef} allTreatmentsRef={allTreatmentsRef}
                  placeholder="Search medicines, doctors..." className="form-control rounded-pill"
                  style={{ paddingLeft: 40 }} onAfterNavigate={closeMobileSearch} />
              </div>
            </div>
          </div>
        </div>
        </div>
        </div>
        </div>
      </header>
    </>
  );
};

export default Header;